import { io } from "socket.io-client";

import { API_BASE_URL } from "../Services/apiService";
import { getToken } from "./sessionStorage";

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
  }

  connect(serverUrl = API_BASE_URL) {
    if (this.socket && this.isConnected) {
      console.log("🔌 Socket already connected");
      return this.socket;
    }

    try {
      this.socket = io(serverUrl, {
        transports: ["websocket", "polling"],
        // The backend only accepts admin/operator sessions on the socket.
        // A callback so every (re)connect attempt sends the current token.
        auth: (cb) => cb({ token: getToken() }),
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5, // ---------------------------------------------------
        timeout: 20000,
      });

      this.socket.on("connect", () => {
        console.log("🟢 Socket connected:", this.socket.id);
        this.isConnected = true;
      });

      this.socket.on("disconnect", (reason) => {
        console.log("🔴 Socket disconnected:", reason);
        this.isConnected = false;

        // ----------------------------------------------------------------------
        if (reason !== "io client disconnect") {
          this.tryReconnect(serverUrl);
        }
      });

      this.socket.on("connect_error", (error) => {
        console.error("❌ Socket connection error:", error);
        this.isConnected = false;

        // ----------------------------------------------------------------------
        this.tryReconnect(serverUrl);
      });

      return this.socket;
    } catch (error) {
      console.error("❌ Error creating socket connection:", error);
      return null;
    }
  }

  // ----------------------------------------------------------------------
  tryReconnect(serverUrl) {
  if (!this.socket || !this.socket.connected) {
    console.log('🔄 Trying to reconnect socket...');
    setTimeout(() => {
      this.connect(serverUrl);
    }, 5000); // retry after 5 seconds
  }
}

getStatus() {
  return this.socket?.connected || false;
}



  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log("🔌 Socket disconnected");
    }
  }

  // Listen for new orders
  onNewOrder(callback) {
    if (this.socket) {
      this.socket.on("NEW_ORDER", (newOrder) => {
        console.log("📦 New order received:", newOrder);
        callback(newOrder);
      });
    }
  }

  // Remove new order listener
  offNewOrder() {
    if (this.socket) {
      this.socket.off("NEW_ORDER");
    }
  }

  // Listen for new analysis items (lab tests ready for analysis)
  onNewAnalysis(callback) {
    if (this.socket) {
      this.socket.on("lab_test_returned", (newAnalysis) => {
        console.log(
          "🧪 New analysis item received via lab_test_returned:",
          newAnalysis
        );
        callback(newAnalysis);
      });
    }
  }

  // Remove new analysis listener
  offNewAnalysis() {
    if (this.socket) {
      this.socket.off("lab_test_returned");
    }
  }

  // Listen for new activities
  onNewActivity(callback) {
    if (this.socket) {
      this.socket.on("NEW_ACTIVITY", (newActivity) => {
        console.log("🔔 New activity received:", newActivity);
        callback(newActivity);
      });
    }
  }

  // Remove new activity listener
  offNewActivity() {
    if (this.socket) {
      this.socket.off("NEW_ACTIVITY");
    }
  }

  // Listen for analysis data updates
  onAnalysisData(callback) {
    if (this.socket) {
      this.socket.on("ANALYSIS_DATA", (analysisData) => {
        console.log(
          "🧪 Analysis data received via ANALYSIS_DATA:",
          analysisData
        );
        callback(analysisData);
      });
    }
  }

  // Remove analysis data listener
  offAnalysisData() {
    if (this.socket) {
      this.socket.off("ANALYSIS_DATA");
    }
  }

  // Get socket instance
  getSocket() {
    return this.socket;
  }

  // Check connection status
  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected;
  }
}

// Export a singleton instance
const socketService = new SocketService();
export default socketService;
